/**
 * Participant tracking store.
 * Manages a record of peerId -> ParticipantInfo with add/remove/update.
 */

import { createStore } from './store'
import type { ParticipantInfo, ParticipantMap } from '../types'

export const participantStore = createStore<{ participants: ParticipantMap }>({
  participants: {},
})

/** Add a participant to the store */
export function addParticipant(
  peerId: string,
  info: Omit<ParticipantInfo, 'peerId'>,
): void {
  const current = participantStore.getState().participants
  if (current[peerId]) {
    console.warn(`[participants] peer already exists: ${peerId}`)
    return
  }
  participantStore.setState({
    participants: {
      ...current,
      [peerId]: { ...info, peerId },
    },
  })
}

/** Remove a participant from the store */
export function removeParticipant(peerId: string): void {
  const current = participantStore.getState().participants
  if (!current[peerId]) {
    console.warn(`[participants] peer not found for removal: ${peerId}`)
    return
  }
  const next = { ...current }
  delete next[peerId]
  participantStore.setState({ participants: next })
}

/** Update a participant's properties (partial merge) */
export function updateParticipant(
  peerId: string,
  update: Partial<ParticipantInfo>,
): void {
  const current = participantStore.getState().participants
  const existing = current[peerId]
  if (!existing) {
    console.warn(`[participants] peer not found for update: ${peerId}`)
    return
  }
  participantStore.setState({
    participants: {
      ...current,
      [peerId]: { ...existing, ...update },
    },
  })
}

/** Get a single participant by peerId */
export function getParticipant(peerId: string): ParticipantInfo | undefined {
  return participantStore.getState().participants[peerId]
}

/** Get all participants as an array */
export function getAllParticipants(): ParticipantInfo[] {
  return Object.values(participantStore.getState().participants)
}

/** Get the count of participants */
export function getParticipantCount(): number {
  return Object.keys(participantStore.getState().participants).length
}

/** Clear all participants (e.g., on meeting end) */
export function clearParticipants(): void {
  participantStore.setState({ participants: {} })
}
